import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { getCurrentTimestamp } from "../../../../Helper/DatePicker/DateConverter";
import FullScreenDialog from "../../../../Components/FullScreenDialog";
import CloseButton from "../../../../Components/CloseButton";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { usePagesContent, useBaseEntityInfo, useBaseEntityIdLabel } from "../../../../Hooks/usePagesContent";
import { getRULBreakdownDrillDownData, getSOHDetailsTableAction } from "../../../../Api/BatteryHealthApi";
import { encryptTheParams } from "../../../../Helper/QueryParams/EncryptDecrypt";
import CODE from "../../../../Static/Constants/StatusCodes";
import {
  SOH_BREAKDOWN_COLUMNS_CONFIG,
  SOH_BREAKDOWN_COLUMN_ORDER,
  getSohBreakdownCustomRenderers,
} from "../SOHBreakdown/SOHBreakdownTableConfig";
import { getRulCustomRenderers } from "../RULDetails/RULDetailsTableConfig";
import { RUL_BUCKET_KEYS } from "../utils/rulConstants";
import { flattenRow, replaceInternalIdColumns, withDeviceIdField } from "../utils/tableUtils";

// ─── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE    = 20;
const DEFAULT_SORT = [{ column: "sohEst", direction: "ASC" }];

// ─── Component ─────────────────────────────────────────────────────────────────
const RULBreakdownDrillDownModal = ({
  open,
  onClose,
  selectedDataPoint,
  globalFilters = {},
}) => {
  const pagesContent = usePagesContent();
  const { baseEntityTypePluralLable } = useBaseEntityInfo();

  const onBatteryIDClick = useCallback((record) => {
    const hash = encryptTheParams({ batteryID: record.batteryID, tabValue: "health" });
    window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
  }, []);

  // Merge SOH breakdown renderers with RUL Details renderers so that
  // all valueRenderingType variants used across org meta configs are covered
  // (vehicle-license-plate, battery-id-link, digital-twin-link, soh-progress-bar, etc.)
  const customRenderers = useMemo(
    () => ({
      ...getSohBreakdownCustomRenderers(onBatteryIDClick),
      ...getRulCustomRenderers(onBatteryIDClick),
    }),
    [onBatteryIDClick]
  );

  // ── orgMeta-derived select + metaInfo (used in API call) ──────────────────
  const [orgMetaSelect,   setOrgMetaSelect]   = useState([]);
  // Always carries the device id, which the id column and the export read.
  const [orgMetaMetaInfo, setOrgMetaMetaInfo] = useState(() => withDeviceIdField([]));
  const deviceIdLabel = useBaseEntityIdLabel("Device ID");
  const [schemaReady,     setSchemaReady]     = useState(false);

  // ── Table state ───────────────────────────────────────────────────────────
  const [tableData,            setTableData]            = useState([]);
  const [total,                setTotal]                = useState(0);
  const [isLoading,            setIsLoading]            = useState(false);
  const [pageNumber,           setPageNumber]           = useState(1);
  const [sortBy,               setSortBy]               = useState(DEFAULT_SORT);
  const [tableColumns,         setTableColumns]         = useState([]);
  const [selectedColumnKeys,   setSelectedColumnKeys]   = useState([]);
  const [initialColumns,       setInitialColumns]       = useState([]);
  const [defaultSelectedKeys,  setDefaultSelectedKeys]  = useState([]);
  const [columnOrder,          setColumnOrder]          = useState([]);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll,          setIsSelectAll]          = useState(false);

  // ── Helper: initialise column state from config + order ───────────────────
  const initColumnsFromConfig = useCallback((config, order) => {
    const cols = getColumnsConfig({ tableColumns: config, tableColumnOrder: order, customRenderers });
    setInitialColumns(cols);
    setTableColumns(cols);
    const keys = cols.map((c) => c.dataIndex);
    setSelectedColumnKeys(keys);
    setDefaultSelectedKeys(keys);
    setColumnOrder(cols.map((c) => c.headerLabel));
  }, [customRenderers]);

  // ── Step 1: Initialise column schema from pagesContent ───────────────────
  useEffect(() => {
    const tableSchema = pagesContent?.metaInfo?.BatteryIntelligence?.RULDrillDown?.table;
    if (tableSchema) {
      setOrgMetaSelect(tableSchema.select   || []);
      setOrgMetaMetaInfo(withDeviceIdField(tableSchema.metaInfo || []));
      // Never the internal batteryID, on screen or in the download / email.
      initColumnsFromConfig(
        replaceInternalIdColumns(tableSchema.columnsConfig, deviceIdLabel, tableSchema.columnOrdering),
        tableSchema.columnOrdering || []
      );
    } else {
      // Fallback to local static config (same columns as SOH breakdown)
      initColumnsFromConfig(
        replaceInternalIdColumns(SOH_BREAKDOWN_COLUMNS_CONFIG, deviceIdLabel, SOH_BREAKDOWN_COLUMN_ORDER),
        SOH_BREAKDOWN_COLUMN_ORDER
      );
    }
    setSchemaReady(true);
  }, [pagesContent, initColumnsFromConfig, deviceIdLabel]);

  // ── Reset when modal closes ───────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setTableData([]);
      setPageNumber(1);
      setSortBy(DEFAULT_SORT);
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
    }
  }, [open]);

  // ── Reset pagination when selected data point changes ─────────────────────
  useEffect(() => {
    if (open && selectedDataPoint) {
      setPageNumber(1);
      setSortBy(DEFAULT_SORT);
    }
  }, [open, selectedDataPoint]);

  // ── Step 2: Fetch table data ──────────────────────────────────────────────
  useEffect(() => {
    if (!open || !selectedDataPoint || !schemaReady) return;

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const rulUnit        = selectedDataPoint?.rulUnit || "months";
        const rulBucketKey   = RUL_BUCKET_KEYS[rulUnit];
        const rulBucketValue = selectedDataPoint?.rulCategory || "";

        const additionalFilters = selectedDataPoint?.locationFilter
          ? [selectedDataPoint.locationFilter]
          : [];

        const res = await getRULBreakdownDrillDownData(
          globalFilters,
          rulBucketKey,
          rulBucketValue,
          orgMetaSelect,
          orgMetaMetaInfo,
          pageNumber,
          PAGE_SIZE,
          sortBy,
          additionalFilters
        );

        if (cancelled) return;

        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const items = res?.response?.data ?? [];
          setTableData(items.map(flattenRow));
          setTotal(res?.response?.paginationInfo?.total ?? items.length);
        } else {
          setTableData([]);
          setTotal(0);
        }
      } catch (err) {
        console.error("RULBreakdownDrillDown: fetch error", err);
        if (!cancelled) { setTableData([]); setTotal(0); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [open, selectedDataPoint, pageNumber, sortBy, schemaReady,
      globalFilters, orgMetaSelect, orgMetaMetaInfo]);

  // ── Table interaction handlers ────────────────────────────────────────────
  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  const handleSortChange = (key, order) => {
    setSortBy([{ column: key, direction: order === "asc" ? "ASC" : "DESC" }]);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const getCurrentSorting = () => {
    if (sortBy.length > 0) return { sortBy: sortBy[0].column, orderBy: sortBy[0].direction.toLowerCase() };
    return {};
  };

  const applySelectedColumns = (newKeys) => {
    const filtered = initialColumns.filter((c) => newKeys.includes(c.dataIndex));
    setTableColumns(filtered);
    setSelectedColumnKeys(newKeys);
    setColumnOrder(filtered.map((c) => c.headerLabel));
  };

  const onColumnOrderChange = (newCols) => {
    setTableColumns(newCols);
    setColumnOrder(newCols.map((c) => c.headerLabel));
  };

  const buildDrillDownFilters = useCallback(() => {
    const rulUnit        = selectedDataPoint?.rulUnit || "months";
    const rulBucketKey   = RUL_BUCKET_KEYS[rulUnit];
    const rulBucketValue = selectedDataPoint?.rulCategory || "";
    const locationFilters = selectedDataPoint?.locationFilter
      ? [selectedDataPoint.locationFilter]
      : [];
    return [
      ...(rulBucketValue ? [{ name: rulBucketKey, value: [rulBucketValue] }] : []),
      ...locationFilters,
    ];
  }, [selectedDataPoint]);

  const buildActionBody = useCallback((actionType, emailParams = {}) => ({
    actionType,
    select: orgMetaSelect,
    groupBy: ["batteryID"],
    metaInfo: orgMetaMetaInfo,
    filters: buildDrillDownFilters(),
    columns: tableColumns.map((c) => ({ dataIndex: c.dataIndex, headerLabel: c.headerLabel })),
    columnOrder: tableColumns.map((c) => c.dataIndex),
    selectAll: isSelectAll,
    resourceIDs: Array.from(totalSelectedRowKeys),
    sortBy: sortBy[0]?.column || "sohEst",
    orderBy: sortBy[0]?.direction || "ASC",
    fileName: `RUL_DrillDown_BatteryList_${getCurrentTimestamp("YYYY-MM-DD")}.csv`,
    ...emailParams,
  }), [orgMetaSelect, orgMetaMetaInfo, buildDrillDownFilters, tableColumns, isSelectAll, totalSelectedRowKeys, sortBy]);

  const getCSVData = useCallback(async () => {
    const res = await getSOHDetailsTableAction(globalFilters, buildActionBody("download"));
    return res?.response?.data ?? [];
  }, [globalFilters, buildActionBody]);

  const onSendEmail = useCallback(async ({ sendTo, sendCc, subject, body }) => {
    await getSOHDetailsTableAction(globalFilters, buildActionBody("mail", {
      emailTo: sendTo,
      emailCc: sendCc,
      subject,
      body,
    }));
  }, [globalFilters, buildActionBody]);

  const getCsvColumnSettings = useCallback(() => ({
    columnOrder: tableColumns.map((c) => c.headerLabel),
  }), [tableColumns]);

  const onSelectChange = (record, selected) => {
    const updated = new Set(totalSelectedRowKeys);
    if (selected) updated.add(record.id);
    else {
      updated.delete(record.id);
      if (isSelectAll) setIsSelectAll(false);
    }
    setTotalSelectedRowKeys(updated);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((r) => r?.id)) : new Set()
    );
  };

  // ── Title path: e.g. "Remaining Useful Life / Bangalore / >4 years" ───────
  const titlePath = useMemo(() => {
    if (!selectedDataPoint) return "";
    const loc = selectedDataPoint.locationLabel || "All";
    const cat = selectedDataPoint.rulCategoryLabel || selectedDataPoint.rulCategory || "";
    return `Remaining Useful Life / ${loc} / ${cat}`;
  }, [selectedDataPoint]);

  return (
    <FullScreenDialog
      open={open}
      handleClose={onClose}
      containerStyles={{ bgcolor: "#FAFAFA", overflowY: "hidden" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "#FAFAFA" }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "1px solid #E6E6E6",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="fw700s20c000000" sx={{ lineHeight: "28px" }}>
              Drill Down:
            </Typography>
            <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
              {titlePath}
            </Typography>
          </Box>
          <CloseButton onClick={onClose} />
        </Box>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2, minHeight: 0 }}>
          <GenericDraggableTableView
            title={`${baseEntityTypePluralLable} List`}
            showPrimaryButton={false}
            count={total || tableData.length}
            tableColumns={tableColumns}
            selectedColumns={selectedColumnKeys}
            tableData={tableData}
            availableEmailDomains={pagesContent?.metaInfo?.availableEmailDomains || []}
            initialColumns={initialColumns}
            applySelectedColumns={applySelectedColumns}
            defaultSelectedColumns={defaultSelectedKeys}
            isLoading={isLoading}
            disablePagination={false}
            pageNumber={pageNumber}
            rowsPerPage={PAGE_SIZE}
            total={total}
            disableSearch={true}
            filters={{}}
            selectedFilters={{}}
            applyFilters={() => {}}
            disableFiltersSettings={true}
            disableColumnSettings={false}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
            applySort={handleSortChange}
            initialSorting={getCurrentSorting()}
            emptyMessage={`No ${baseEntityTypePluralLable.toLowerCase()} found for the selected RUL category`}
            containerStyles={{ backgroundColor: "#FFFFFF", borderRadius: "8px", border: "1px solid #E6E6E6" }}
            getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
            getTotalRowSelectionCount={() =>
              isSelectAll ? (total || tableData.length) : totalSelectedRowKeys.size
            }
            rowSelection={{
              selectedRowKeys: Array.from(totalSelectedRowKeys),
              onSelect: onSelectChange,
              onSelectAll: onSelectAllChange,
            }}
            onClearSelection={() => { setTotalSelectedRowKeys(new Set()); setIsSelectAll(false); }}
            onPageChange={handlePageChange}
            getCSVData={getCSVData}
            onSendEmail={onSendEmail}
            getCsvColumnSettings={getCsvColumnSettings}
          />
        </Box>

      </Box>
    </FullScreenDialog>
  );
};

export default RULBreakdownDrillDownModal;
